import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Video, FileText, ClipboardList, FolderGit2, HeadphonesIcon, ChevronRight, Users } from 'lucide-react';

const cards = [
  {
    id: 'upcoming',
    label: 'Classes',
    description: 'View your scheduled live sessions and join them in real time.',
    icon: Calendar,
    accent: 'bg-blue-500/10 text-blue-600',
    border: 'hover:border-blue-400/40',
  },
  {
    id: 'recordings',
    label: 'Recordings',
    description: 'Watch past class recordings at your own pace, anytime.',
    icon: Video,
    accent: 'bg-purple-500/10 text-purple-600',
    border: 'hover:border-purple-400/40',
  },
  {
    id: 'notes',
    label: 'Notes',
    description: 'Access study materials and notes shared by your instructor.',
    icon: FileText,
    accent: 'bg-emerald-500/10 text-emerald-600',
    border: 'hover:border-emerald-400/40',
  },
  {
    id: 'assignments',
    label: 'Assignments',
    description: 'Submit your work and track feedback on every task.',
    icon: ClipboardList,
    accent: 'bg-orange-500/10 text-orange-600',
    border: 'hover:border-orange-400/40',
  },
  {
    id: 'projects',
    label: 'Projects',
    description: 'Upload your final project submissions for review.',
    icon: FolderGit2,
    accent: 'bg-yellow-500/10 text-yellow-600',
    border: 'hover:border-yellow-400/40',
  },
  {
    id: 'support',
    label: 'Support',
    description: 'Raise a query or get help from the mentor team.',
    icon: HeadphonesIcon,
    accent: 'bg-rose-500/10 text-rose-600',
    border: 'hover:border-rose-400/40',
  },
];

export default function CourseHome({ course, onSelect }) {
  const title     = course?.course_title || course?.title || 'Your Course';
  const batchName = course?.batch_name;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass rounded-2xl border border-white/80 p-6 relative overflow-hidden">
        <div
          className="absolute top-0 right-0 w-56 h-56 rounded-full opacity-10 pointer-events-none"
          style={{ background: 'radial-gradient(circle, #FED202 0%, transparent 70%)', transform: 'translate(30%,-30%)' }}
        />
        <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-1">Course Dashboard</p>
        <h2 className="font-heading text-2xl font-bold tracking-tight">{title}</h2>

        {/* Batch badge — shown prominently when the student has a batch */}
        {batchName ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.15 }}
            className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/25"
          >
            <Users className="w-3.5 h-3.5 text-primary" />
            <span className="text-sm font-semibold text-primary">{batchName}</span>
            <span className="text-xs text-muted-foreground">· Your Batch</span>
          </motion.div>
        ) : (
          <p className="text-muted-foreground text-sm mt-1">Pick a section below to get started.</p>
        )}
      </div>

      {/* Batch info strip — extra emphasis when batch is assigned */}
      {batchName && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="flex items-center gap-3 px-4 py-3 rounded-xl bg-primary/5 border border-primary/15"
        >
          <div className="w-8 h-8 rounded-lg bg-primary/15 flex items-center justify-center flex-shrink-0">
            <Users className="w-4 h-4 text-primary" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground">
              You're in <span className="text-primary">{batchName}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              Content below is curated specifically for your batch, plus all shared materials.
            </p>
          </div>
        </motion.div>
      )}

      {/* Section cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {cards.map((card, i) => (
          <motion.button
            key={card.id}
            onClick={() => onSelect(card.id)}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07, duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -4 }}
            whileTap={{ scale: 0.98 }}
            className={`text-left w-full glass rounded-2xl border border-white/80 p-5 shadow-sm group transition-all duration-200 ${card.border}`}
          >
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${card.accent} transition-transform group-hover:scale-110`}>
              <card.icon className="w-5 h-5" />
            </div>
            <div className="flex items-start justify-between gap-2">
              <div>
                <h3 className="font-heading font-bold text-base">{card.label}</h3>
                <p className="text-sm text-muted-foreground mt-1 leading-snug">{card.description}</p>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground/40 shrink-0 mt-0.5 group-hover:text-primary transition-colors" />
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
